/**
 * Form field validators.
 *
 * Each validator is a factory: it takes the message to show and returns a
 * function that returns that message when the value is invalid, or
 * `undefined` when it is valid — the shape React Final Form expects.
 *
 * Only the validators the forms actually use are kept. The template this
 * started from shipped a dozen more (array checkboxes, select trees,
 * integer ranges, business URLs) for marketplace listing forms that do not
 * exist here; they were dead code and are gone.
 */

import { isValidPhoneNumber } from 'react-phone-number-input';

const isNonEmptyString = val => typeof val === 'string' && val.trim().length > 0;

/** React Final Form reads `undefined` as "no error". */
const VALID = undefined;

export const required = message => value => {
  if (typeof value === 'undefined' || value === null) return message;
  if (typeof value === 'string') return isNonEmptyString(value) ? VALID : message;
  /*
   * An empty array is empty, but it is also an object, so the final `return
   * VALID` below used to accept it. React Final Form gives a checkbox that
   * carries a `value` prop an ARRAY, not a boolean, and unticking the box
   * leaves `[]` — which this validator passed, letting the consent box be
   * submitted unticked.
   */
  if (Array.isArray(value)) return value.length > 0 ? VALID : message;
  return VALID;
};

/**
 * A consent box: only boolean `true` passes.
 *
 * `required` is the wrong tool for a checkbox. It asks "is there a value",
 * and `false` IS a value — as are `[]`, `0` and `'false'`. This asks the only
 * question consent allows: was it actually ticked.
 */
export const accepted = message => value => (value === true ? VALID : message);

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const emailFormatValid = message => value =>
  value && EMAIL_RE.test(value) ? VALID : message;

/*
 * Validated with the same library that renders the input, so the two can
 * never disagree about what counts as a valid number.
 */
export const phoneFormatValid = message => value => {
  if (!value) return VALID;
  return isValidPhoneNumber(value) ? VALID : message;
};

/** Runs validators left to right and returns the first error. */
export const composeValidators = (...validators) => value =>
  validators.reduce((error, validator) => error || validator(value), VALID);
